import type { PlayerWithMarkets, LineupInfo } from '@sharpr/types';
import type { ResearchTarget } from '@/types/research';
import type { PropRow } from '@/components/client/VirtualizedPropTable';
import { resolvePositionType } from '@/utils/playerTaxonomy';

type BestMarketHint = NonNullable<PlayerWithMarkets['bestMarket']> | null | undefined;

interface MarketAggregate {
  marketLines: Record<string, number>;
  marketProjections: Record<string, number>;
}

/** Per-market best-line + projection map for a single `PlayerWithMarkets`, used by the research
 *  target factory, the OverlayTarget call sites and the table's rows. */
export function aggregateMarketLines(player: PlayerWithMarkets): MarketAggregate {
  const marketLines: Record<string, number> = {};
  const marketProjections: Record<string, number> = {};
  for (const [mkt, mktData] of Object.entries(player.markets)) {
    if (!mktData) continue;
    const line = mktData.bestLines?.over?.line
      ?? mktData.bestLines?.under?.line
      ?? mktData.books.find((b) => !b.isRemoved)?.line;
    if (line != null) marketLines[mkt] = line;
    if (mktData.projection != null) marketProjections[mkt] = mktData.projection;
  }
  return { marketLines, marketProjections };
}

/**
 * Canonical builder for every surface that opens the Player Research overlay.
 * `bestMarket` (props grid) populates marketCode/marketLine/projectedMinutes; the slate
 * pitcher card omits it. For TWPs, lineup/teamNotes come from the active market when given
 * (pitcher market = opposing lineup, batter market = own), else player-level fallback.
 */
export function buildResearchTarget(
  player: PlayerWithMarkets,
  opts: { bestMarket?: BestMarketHint } = {},
): ResearchTarget {
  const bestMarket = opts.bestMarket ?? null;
  const bestMarketData = bestMarket?.market ? player.markets[bestMarket.market] : undefined;
  const { marketLines, marketProjections } = aggregateMarketLines(player);

  return {
    playerId: player.id,
    playerName: player.name,
    team: player.team,
    imageUrl: player.imageUrl,
    position: player.position,
    positionType: resolvePositionType(player.position, bestMarket?.market),
    opponent: player.game?.opp,
    gameId: player.game?.id,
    isHome: player.game?.isHome,
    teamNotes: bestMarketData?.teamNotes ?? player.teamNotes,
    playerNotes: player.playerNotes,
    lineup: bestMarketData?.lineup ?? player.lineup,
    marketLine: bestMarket?.line?.line ?? null,
    marketCode: bestMarket?.market,
    projectedMinutes: bestMarket?.market ? player.markets[bestMarket.market]?.projectedMinutes : undefined,
    marketLines,
    marketProjections,
  };
}

/**
 * Minimal fallback target from slate/lineup-row data — used by the swap-target resolver
 * when the player isn't in a `PlayerWithMarkets[]` lookup (props not ingested yet, or a
 * slate-only surface). The side-specific wrappers below are the public API.
 *
 * `lineup` semantics by side:
 *   - batter: the batter's OWN team's lineup (what the batter Matchup tab
 *     needs to compose its opposing-pitcher view)
 *   - pitcher: the OPPOSING team's lineup (what the pitcher Matchup tab needs
 *     to render the lineup table)
 */
function buildFallbackTarget(
  player: { playerId: string; name: string; position?: string },
  team: string,
  positionType: 'pitcher' | 'batter',
  opts: { opponent?: string; lineup?: LineupInfo; imageUrl?: string } = {},
): ResearchTarget {
  return {
    playerId: player.playerId,
    playerName: player.name,
    team,
    position: player.position ?? (positionType === 'pitcher' ? 'P' : undefined),
    positionType,
    opponent: opts.opponent,
    lineup: opts.lineup,
    imageUrl: opts.imageUrl,
  };
}

export function buildBatterFallbackTarget(
  batter: { playerId: string; name: string; position?: string },
  team: string,
  opts: { opponent?: string; lineup?: LineupInfo; imageUrl?: string } = {},
): ResearchTarget {
  return buildFallbackTarget(batter, team, 'batter', opts);
}

export function buildPitcherFallbackTarget(
  pitcher: { playerId: string; name: string; position?: string },
  team: string,
  opts: { opponent?: string; lineup?: LineupInfo; imageUrl?: string } = {},
): ResearchTarget {
  return buildFallbackTarget(pitcher, team, 'pitcher', opts);
}

/**
 * Resolve an overlay swap request to a full `ResearchTarget`. Prefer the
 * caller's `PlayerWithMarkets` lookup so the resulting target carries the
 * canonical market lines + projections + lineup; fall back to the overlay-
 * provided shape only when the player isn't in the visible payload (e.g.
 * a pitcher whose props haven't been ingested yet, sport mismatch, etc.).
 *
 * Wired into the overlay's `onSwapTarget` by both `PlayerSelectionView` and
 * `SlateView` — keeps the lookup-then-fallback policy in one place.
 */
export function resolveSwapTarget(
  req: { playerId: string; fallback: ResearchTarget },
  lookup: (id: string) => PlayerWithMarkets | undefined,
): ResearchTarget {
  const player = lookup(req.playerId);
  return player ? buildResearchTarget(player) : req.fallback;
}

/**
 * Same as buildResearchTarget but for the table view, where the caller has a flat PropRow
 * (carrying its player's per-market maps) instead of a nested PlayerWithMarkets.
 */
export function buildResearchTargetFromPropRow(row: PropRow): ResearchTarget {
  const marketLine = row.bestLines?.over?.line
    ?? row.bestLines?.under?.line
    ?? row.books.find((b) => !b.isRemoved)?.line;

  return {
    playerId: row.playerId,
    playerName: row.primaryInfo.title,
    team: row.team ?? '',
    imageUrl: row.primaryInfo.imageUrl,
    position: row.primaryInfo.position,
    positionType: resolvePositionType(row.primaryInfo.position, row.projectionInfo.market),
    opponent: row.opponent,
    gameId: row.gameId,
    isHome: row.isHome,
    teamNotes: row.teamNotes,
    playerNotes: row.playerNotes,
    lineup: row.lineup,
    marketLine: marketLine ?? null,
    marketCode: row.projectionInfo.market,
    projectedMinutes: row.projectionInfo.minutes,
    marketLines: row.marketLines,
    marketProjections: row.marketProjections,
  };
}
